import React from 'react';
import { Avatar, Button, Divider, Drawer, Select, Space, Tag } from 'antd';
import {
  CrownOutlined,
  EditOutlined,
  LogoutOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';

const PLAN_LABEL: Record<string, string> = {
  free: '免费版',
  basic: '基础版',
  pro: '专业版',
  enterprise: '企业版',
};

const PLAN_COLOR: Record<string, string> = {
  free: '#8C8C8C',
  basic: '#1677FF',
  pro: '#F6AD55',
  enterprise: '#2453D4',
};

interface MobileAccountPanelProps {
  open: boolean;
  onClose: () => void;
}

const MobileAccountPanel: React.FC<MobileAccountPanelProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentTenant, tenants, switchTenant } = useTenant();

  if (!user) return null;

  const displayName = user.real_name || user.username || user.email.split('@')[0];
  const plan = currentTenant?.plan || 'free';

  const handleNavigate = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { icon: <UserOutlined style={{ color: '#1677FF' }} />, label: '个人账户', path: '/personal-account' },
    { icon: <CrownOutlined style={{ color: '#F6AD55' }} />, label: '套餐管理', path: '/subscription' },
    { icon: <EditOutlined style={{ color: '#1677FF' }} />, label: '修改信息 / 密码', path: '/settings/account' },
    { icon: <SettingOutlined style={{ color: '#1677FF' }} />, label: '机构设置', path: '/settings/tenant' },
  ];

  return (
    <Drawer
      placement="right"
      width="100%"
      open={open}
      onClose={onClose}
      title="账号与工作区"
      styles={{
        body: { paddingTop: 8, paddingBottom: 24 },
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Avatar
          size={56}
          icon={<UserOutlined />}
          src={user.avatar_url}
          style={{ background: '#1677FF', flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={{ color: '#061529', fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
            {displayName}
          </div>
          <div style={{ color: '#7A869A', fontSize: 13, marginTop: 4, wordBreak: 'break-all' }}>
            {user.email}
          </div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              marginTop: 8,
              fontSize: 11,
              color: PLAN_COLOR[plan] || '#8C8C8C',
              background: `${PLAN_COLOR[plan] || '#8C8C8C'}14`,
              padding: '4px 10px',
              borderRadius: 999,
            }}
          >
            <CrownOutlined />
            {PLAN_LABEL[plan] || plan}
          </span>
        </div>
      </div>

      {tenants.length > 1 ? (
        <>
          <Divider />
          <div style={{ marginBottom: 8, color: '#7A869A', fontSize: 12, fontWeight: 600 }}>
            当前机构
          </div>
          <Space size={10} style={{ width: '100%', alignItems: 'flex-start' }}>
            <TeamOutlined style={{ color: '#1677FF', marginTop: 11 }} />
            <Select
              value={currentTenant?.id}
              onChange={switchTenant}
              style={{ flex: 1 }}
              options={tenants.map((tenant) => ({
                value: tenant.id,
                label: (
                  <Space>
                    <span>{tenant.name}</span>
                    <Tag color={tenant.plan ? 'blue' : 'default'} style={{ marginRight: 0 }}>
                      {tenant.plan}
                    </Tag>
                    {tenant.status === 'suspended' && <Tag color="orange" style={{ marginRight: 0 }}>只读</Tag>}
                  </Space>
                ),
              }))}
            />
          </Space>
        </>
      ) : null}

      <Divider />

      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {menuItems.map((item) => (
          <Button
            key={item.path}
            block
            size="large"
            style={{
              height: 52,
              borderRadius: 16,
              textAlign: 'left',
              justifyContent: 'flex-start',
              fontWeight: 600,
            }}
            icon={item.icon}
            onClick={() => handleNavigate(item.path)}
          >
            {item.label}
          </Button>
        ))}
      </Space>

      <Divider />

      <Button
        block
        danger
        size="large"
        icon={<LogoutOutlined />}
        onClick={handleLogout}
        style={{ height: 52, borderRadius: 16, fontWeight: 700 }}
      >
        退出登录
      </Button>
    </Drawer>
  );
};

export default MobileAccountPanel;
